import React, { useState, useContext, useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Select from "react-select";
import DatePicker, { registerLocale } from "react-datepicker";

import "react-datepicker/dist/react-datepicker.css";
import es from "date-fns/locale/es";

import { useTerceros } from "../../hooks_cache/useTerceros.js";
import { useTiposDte } from "../../hooks_cache/useTiposDte.js";
import { useActividadesEconomicas } from "../../hooks_cache/useActividadesEconomicas";

import { useSelector } from "react-redux";
import { AppSettings } from "./../../config/app-settings.js";

import DteFormFactory from "./dte_components/DteFormFactory.js";

import { useComprobanteById } from "../../hooks_cache/useComprobantes.js";
import Swal from "sweetalert2";

registerLocale("es", es);

// 📌 Definir reglas de validación de manera dinámica
const reglasValidacion = {
  "03": ["nit", "nrc", "correo", "direccion_completa", "id_departamento", "id_municipio"], // Para CCFE: requiere NIT y NRC
  "01": ["nombre", "id_tipoDocumento", "numDocumento", "correo", "direccion_completa", "id_departamento", "id_municipio"], // Para FE: requiere Nombre y Dirección
  "05": ["nombre", "correo", "direccion_completa", "id_departamento", "id_municipio"], // Para NCE: requiere NIT y NRC
  11: ["nombre", "id_tipoDocumento", "numDocumento", "correo", "direccion_completa", "id_departamento", "id_municipio"],
};

function RegistrarComprobante() {
  const context = useContext(AppSettings);
  const { empresas } = useSelector((state) => state.auth);

  const [searchParams] = useSearchParams();
  const comprobante_id = searchParams.get("comprobanteId");
  const { data: comprobanteLoaded, isFetching } = useComprobanteById(comprobante_id);

  const { data: actividades } = useActividadesEconomicas();
  const { data: terceros } = useTerceros();
  const { data: tiposDte } = useTiposDte();

  const [terceroSelected, setTerceroSelected] = useState(null);
  const [tipoDteSelected, setTipoDteSelected] = useState(null);
  const [errores, setErrores] = useState({});

  const [fechaEmision, setFechaEmision] = useState(new Date());

  useEffect(() => {
    if (isFetching) {
      Swal.fire({
        title: "Cargando comprobante...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });
    } else {
      Swal.close();
    }
  }, [isFetching]);

  // Memoización de datos para evitar recalcular en cada render
  const customTercero = useMemo(
    () =>
      terceros
        ?.filter((t) => t.es_cliente)
        .map((t) => ({
          ...t,
          value: t.id,
          label: `${t.nrc} | ${t.nombre}`,
        })) || [],
    [terceros]
  );

  const customTiposDte = useMemo(
    () =>
      tiposDte?.map((t) => ({
        ...t,
        value: t.id,
        label: `${t.codigo} | ${t.descripcion}`,
      })) || [],
    [tiposDte]
  );

  useEffect(() => {
    if (comprobante_id) {
      setTerceroSelected(customTercero.find((t) => t.id === comprobanteLoaded.Tercero.id) || null);
      setTipoDteSelected(customTiposDte.find((t) => t.id === comprobanteLoaded.TipoDTE.id) || null);
    }
  }, [comprobante_id, comprobanteLoaded]);

  // 📌 Validar campos requeridos cuando cambia el tipo de DTE
  useEffect(() => {
    if (!tipoDteSelected || !terceroSelected) return;

    const camposRequeridos = reglasValidacion[tipoDteSelected.codigo] || [];
    let nuevosErrores = {};

    // Recorrer los campos requeridos y validar si están presentes
    camposRequeridos.forEach((campo) => {
      if (!terceroSelected[campo]) {
        nuevosErrores[campo] = `El campo ${campo.replace("_", " ")} es obligatorio.`;
      }
    });
    setErrores(nuevosErrores);
  }, [tipoDteSelected, terceroSelected]);

  useEffect(() => {
    context.handleSetAppContentClass("bg-component");
    return () => {
      context.handleSetAppContentClass("");
    };
  }, []);

  return (
    <div>
      <div className="row">
        <div
          className="col-12 col-md-4 "
          style={{
            borderRight: "1px solid rgba(173, 181, 189, 0.46)",
            height: "170px",
          }}
        >
          <div className="col-12 col-md-12 mb-2">
            <label className="form-label">TIPO COMPROBANTE: *</label>
            <Select
              value={tipoDteSelected}
              options={customTiposDte}
              isSearchable
              placeholder="Seleccione un tipo de DTE"
              onChange={(selected) => setTipoDteSelected(selected)}
            />
          </div>
          <div className="col-12 col-md-12 mb-2">
            <label className="form-label">Fecha emision:</label>
            <DatePicker
              selected={fechaEmision}
              dateFormat="dd/MM/yyyy"
              locale="es"
              onChange={(date) => setFechaEmision(date)}
              className="form-control"
              disabled={true}
            />
          </div>
        </div>

        <div className="col-12 col-md-8">
          <div className="row">
            <div className="col-md-10 col-9">
              <label className="form-label">Nombre / Razon social:</label>

              <Select
                value={terceroSelected}
                options={customTercero}
                isSearchable
                placeholder="Seleccione una razon social"
                onChange={(selected) => setTerceroSelected(selected)}
              />
            </div>

            <div className="col-md-2 col-3">
              <Link to={`/tercero`} className="btn btn-green btn-xs mt-4">
                <span className="d-flex align-items-center text-start">
                  <i className="fas fa-plus me-2 text-black"></i>
                  <span>
                    <span className="d-block">Nuevo </span>
                    <span className="d-block">Cliente</span>
                  </span>
                </span>
              </Link>
            </div>
          </div>
          <div className="row mt-2px">
            <div className="invoice-from">
              <small>Datos de cliente</small>
              <address className="mt-2px mb-5px">
                <Link to={`/tercero/${terceroSelected?.value}`} className="text-dark fw-bold">
                  <strong>{terceroSelected?.nombre}</strong>
                </Link>
                <br />
                <strong className="text-dark">Dirección:</strong> {terceroSelected?.direccion_completa || "N/A"}
                <br />
                <strong className="text-dark">Registro:</strong> {terceroSelected?.nrc || "N/A"}
                <br />
                <strong className="text-dark">NIT:</strong> {terceroSelected?.nit || "N/A"}
                <br />
                <strong className="text-dark">Giro:</strong>{" "}
                {actividades?.find((a) => a.id === terceroSelected?.id_actividadEconomica)?.["descripcion"] || "N/A"}
                <br />
              </address>

              {Object.keys(errores).length > 0 && (
                <div className="alert alert-danger fade show mb-0 ">
                  <strong>FALTA DE DATOS: </strong>
                  Este cliente necesita actualizar datos para poder emitir el comprobante seleccionado
                  <Link to={`/tercero/${terceroSelected?.value}`} className="text-dark fw-bold">
                    <strong> Click Aqui para actualizar datos!</strong>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <hr />

      {tipoDteSelected && (
        <DteFormFactory
          emisor={empresas[0]}
          tercero={terceroSelected}
          tipoDteSelected={tipoDteSelected}
          itemsPrecargados={comprobanteLoaded?.TipoDTE?.codigo === tipoDteSelected?.codigo ? comprobanteLoaded?.DetalleComprobantes : []}
          isTerceroOk={Object.keys(errores).length == 0}
        />
      )}
    </div>
  );
}

export default RegistrarComprobante;
